import { useEffect, useMemo, useState } from 'react'
import {
  Banner,
  Button,
  Chip,
  DataTable,
  Icon,
  Meter,
  Modal,
  Skeleton,
  Spinner,
  useToast,
  type Column,
} from '@w7/ui'
import { Amount, Money, exportCsv } from '@/app/bits'
import { useBulkSettle, useDownline, type BulkRunRow, type BulkRunState } from '@/app/queries'
import { round2 } from '@/lib/book'
import { EMPTY, count, isoDay, money } from '@/lib/format'
import type { Account } from '@/data/types'
import { planSettlement, planTotals, type PlannedRow } from './settle'
import './downline.css'

/**
 * Bulk settle — plan, run, report.
 *
 * The product being replaced asked "Are you sure you want to Bulk Transfer?"
 * with a Yes and a No, and then moved ₹5,62,820 across two accounts and said
 * "Transferred Successfully". That is not good enough for money. Here the
 * operator reads what is about to happen BEFORE it happens, watches it happen
 * one row at a time, and reads what actually happened afterwards with a ledger
 * reference against every row.
 *
 * Partial by policy: a row that cannot move its whole amount moves what the
 * real caps allow and reports the remainder. Refusing to move ₹12,400 because
 * ₹14,500 was asked for leaves the operator worse off than they started.
 */

/** Outcomes a retry is allowed to touch. A settled row is never re-submitted. */
const RETRYABLE = new Set<BulkRunRow['outcome']['state']>(['partial', 'failed', 'not-attempted'])

type View = 'plan' | 'run' | 'report'

export function BulkSettleDialog({
  self,
  selected,
  onClose,
  onDone,
}: {
  self: Account
  /** The selection AS TICKED. Only used to detect what has moved since. */
  selected: Account[]
  onClose: () => void
  onDone: () => void
}) {
  const toast = useToast()
  const bulk = useBulkSettle()
  const [view, setView] = useState<View>('plan')
  const [retrying, setRetrying] = useState(false)

  /**
   * FRESH FIGURES FIRST. The money invariant applies to a plan as much as to a
   * table: every figure on screen is a ledger projection, including the ones
   * you are about to act on. Mounting this observer refetches (the query is
   * stale the moment it lands), so the plan is built off the ledger as of the
   * click, never off the snapshot in the selection map.
   */
  const pool = useDownline({ directOnly: true, pageSize: 500 })
  const fresh = useMemo(
    () => new Map((pool.data?.rows ?? []).map((row) => [row.id, row])),
    [pool.data],
  )

  const retryIds = useMemo(
    () =>
      retrying
        ? (bulk.state?.rows ?? [])
            .filter((row) => RETRYABLE.has(row.outcome.state))
            .map((row) => row.account.id)
        : null,
    [retrying, bulk.state],
  )

  /** The accounts this plan covers, at their CURRENT figures. */
  const targets = useMemo(() => {
    const wanted = retryIds ?? selected.map((account) => account.id)
    return wanted
      .map((id) => fresh.get(id))
      .filter((account): account is Account => Boolean(account) && account!.settlement !== 0)
  }, [retryIds, selected, fresh])

  /** Selected rows that are no longer plannable, each with the reason why. */
  const gone = useMemo(() => {
    const wanted = retryIds ?? selected.map((account) => account.id)
    return wanted.flatMap((id) => {
      const before = selected.find((account) => account.id === id)
      const now = fresh.get(id)
      const username = now?.username ?? before?.username ?? id
      if (!now) {
        return [{ username, reason: 'is no longer one of your direct children' }]
      }
      if (now.settlement === 0) {
        return [{ username, reason: 'has already settled to zero' }]
      }
      return []
    })
  }, [retryIds, selected, fresh])

  /** How many rows changed under the operator between ticking and opening. */
  const drifted = useMemo(() => {
    if (retryIds) return 0
    return selected.filter((before) => {
      const now = fresh.get(before.id)
      return now && round2(now.settlement) !== round2(before.settlement)
    }).length
  }, [retryIds, selected, fresh])

  const plan = useMemo(() => planSettlement(self, targets), [self, targets])
  const totals = useMemo(() => planTotals(plan), [plan])
  const moving = plan.filter((row) => row.planned > 0)

  /**
   * One runKey per plan, minted when the confirm step is ready. A retry keeps
   * the runKey it was given — that is what makes a replayed row replay rather
   * than move twice — so `prepare` is deliberately NOT called on the retry path.
   */
  const { prepare, reseed } = bulk
  useEffect(() => {
    // Only ever on the plan step. A background refetch during the run or on the
    // report would otherwise re-plan, re-mint the runKey and wipe the outcomes
    // of rows that have already moved money.
    if (pool.isLoading || view !== 'plan') return

    const rows = plan.map((row) => ({
      account: row.account,
      direction: row.direction,
      intended: row.intended,
      planned: row.planned,
      capReason: row.capReason,
      warnings: row.warnings,
    }))

    // Both branches send the SAME freshly-planned rows; they differ only in what
    // they are allowed to disturb. `prepare` mints a runKey and resets outcomes,
    // which is right for a new plan and catastrophic for a retry — a reused
    // runKey is what makes a replayed row replay rather than move twice.
    // `reseed` keeps both and updates only the figures, so the amount submitted
    // is the amount this confirm step is showing.
    if (retrying) reseed(rows)
    else prepare(rows)
  }, [plan, prepare, reseed, retrying, pool.isLoading, view])

  const state = bulk.state
  const running = state?.status === 'running' || state?.status === 'stopping'

  const start = async () => {
    setView('run')
    if (retrying) await bulk.retry(self)
    else await bulk.start(self)
    setRetrying(false)
    setView('report')
  }

  /**
   * The modal cannot be dismissed mid-run. Escape and the scrim both route
   * through `onClose`, so intercepting here keeps the kit's `useDismissable`
   * untouched rather than teaching it about a busy state.
   */
  const guardedClose = () => {
    if (running) {
      toast.push({
        tone: 'warn',
        message: 'The run is still going',
        detail: 'It will finish on its own, or you can stop it after the current row.',
      })
      return
    }
    onClose()
  }

  if (view === 'run' || running) {
    return (
      <RunningView
        state={state}
        busy={running}
        onStop={bulk.stop}
        onClose={guardedClose}
        stopping={state?.status === 'stopping'}
      />
    )
  }

  if (view === 'report' && state) {
    return (
      <ReportView
        state={state}
        onClose={guardedClose}
        onRetry={() => {
          setRetrying(true)
          setView('plan')
        }}
        onDone={() => {
          const report = summarise(state)
          if (report.outstanding === 0 && report.settled === report.total) {
            toast.success(
              `Settled ${count(report.settled)} of ${count(report.total)}`,
              `${money(report.moved)} moved`,
            )
          } else {
            toast.push({
              tone: 'warn',
              message: `Settled ${count(report.settled)} of ${count(report.total)}`,
              detail: `${money(report.moved)} moved · ${money(report.outstanding)} still outstanding across ${count(report.openRows)} ${report.openRows === 1 ? 'account' : 'accounts'}`,
            })
          }
          bulk.reset()
          onDone()
        }}
      />
    )
  }

  return (
    <Modal
      open
      size="lg"
      onClose={guardedClose}
      title={
        retrying
          ? `Retry ${count(plan.length)} ${plan.length === 1 ? 'account' : 'accounts'}`
          : `Settle ${count(plan.length + gone.length)} ${plan.length + gone.length === 1 ? 'account' : 'accounts'} to zero`
      }
      subtitle="Nothing moves until you confirm. Each row is checked against its own cap as it goes."
      footer={
        <>
          <span className="w7-spacer" />
          <Button variant="secondary" onClick={guardedClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon="transfers"
            busy={pool.isLoading}
            disabled={moving.length === 0}
            onClick={start}
          >
            {moving.length === 0
              ? 'Nothing can move'
              : `Move ${money(totals.planned)} across ${count(moving.length)} ${moving.length === 1 ? 'account' : 'accounts'}`}
          </Button>
        </>
      }
    >
      <div className="w7-col" style={{ gap: 'var(--w7-s4)' }}>
        {pool.isLoading ? (
          <>
            <Skeleton height={54} />
            <Skeleton height={180} />
          </>
        ) : (
          <>
            {drifted > 0 ? (
              <Banner tone="warn">
                {count(drifted)} {drifted === 1 ? 'account has' : 'accounts have'} moved since you
                selected {drifted === 1 ? 'it' : 'them'}. The amounts below are current.
              </Banner>
            ) : null}

            <dl className="w7-bulk-strip">
              {/* Toned by direction, not by sign: "Pay −₹1,84,000" reads as a
                  refund. The heading names the direction, the colour confirms it. */}
              <div className="w7-bulk-strip-item">
                <dt className="w7-micro">Collect</dt>
                <dd>
                  <Money value={totals.collected} tone="profit" />
                </dd>
              </div>
              <div className="w7-bulk-strip-item">
                <dt className="w7-micro">Pay</dt>
                <dd>
                  <Money value={totals.paid} tone="loss" />
                </dd>
              </div>
              <div className="w7-bulk-strip-item">
                <dt className="w7-micro">Net</dt>
                <dd>
                  <Money value={totals.net} signed />
                </dd>
              </div>
              <div className="w7-bulk-strip-item">
                <dt className="w7-micro">Outcome</dt>
                <dd className="w7-bulk-strip-note">
                  {count(totals.full)} will settle in full · {count(totals.partial)} partially ·{' '}
                  {count(totals.skipped + gone.length)} will not move
                </dd>
              </div>
            </dl>

            {moving.length > 0 ? (
              <div className="w7-bulk-group">
                <p className="w7-label">Will move ({count(moving.length)})</p>
                <PlanTable rows={moving} />
              </div>
            ) : null}

            {totals.skipped + gone.length > 0 ? (
              <div>
                <p className="w7-label">Will not move ({count(totals.skipped + gone.length)})</p>
                {/* A plain list, not a second table: these rows are not part of
                    the run, and a table would imply they are. */}
                <ul className="w7-bulk-skiplist">
                  {plan
                    .filter((row) => row.planned === 0)
                    .map((row) => (
                      <li key={row.account.id}>
                        {row.account.username} — {row.skipReason}. {money(row.intended)} stays
                        outstanding.
                      </li>
                    ))}
                  {gone.map((row) => (
                    <li key={row.username}>
                      {row.username} — {row.reason}, so it is not part of this run.
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        )}
      </div>
    </Modal>
  )
}

/* ------------------------------------------------------------------ plan -- */

function PlanTable({ rows }: { rows: PlannedRow[] }) {
  const totals = planTotals(rows)
  const columns: Column<PlannedRow>[] = [
    {
      id: 'account',
      header: 'Account',
      width: 180,
      total: <span className="w7-dim">In the order they will run</span>,
      cell: (row) => <span style={{ fontWeight: 600 }}>{row.account.username}</span>,
    },
    {
      id: 'direction',
      header: 'Direction',
      width: 96,
      // Muted, never the money palette. The amount columns carry the colour;
      // a green "Take" chip beside a green figure says the same thing twice.
      cell: (row) => <Chip tone="muted" size="sm">{row.direction === 'take' ? 'Take' : 'Give'}</Chip>,
    },
    {
      id: 'outstanding',
      header: 'Outstanding',
      numeric: true,
      cell: (row) => <Money value={signed(row.direction, row.intended)} signed />,
    },
    {
      id: 'planned',
      header: 'Will move',
      numeric: true,
      total: <Amount value={totals.planned} />,
      cell: (row) => <Money value={signed(row.direction, row.planned)} signed />,
    },
    {
      id: 'remaining',
      header: 'Left over',
      numeric: true,
      // A dash means "nothing left". A ₹0.00 would read as a figure someone
      // computed and might still chase — true of the total as much as the cell.
      total:
        totals.intended > totals.planned ? (
          <Amount value={round2(totals.intended - totals.planned)} />
        ) : (
          EMPTY
        ),
      cell: (row) => (row.remaining > 0 ? <Amount value={row.remaining} /> : EMPTY),
    },
    {
      id: 'note',
      header: 'Note',
      cell: (row) =>
        row.warnings.length > 0 ? (
          <span className="w7-row" style={{ gap: 4, whiteSpace: 'normal' }}>
            <Icon name="warning" size={12} style={{ color: 'var(--w7-warn)' }} />
            {row.warnings[0]}
          </span>
        ) : row.capReason ? (
          <span style={{ whiteSpace: 'normal' }}>{capitalise(row.capReason)}</span>
        ) : (
          <span className="w7-dim">Settles in full</span>
        ),
    },
  ]

  return (
    <DataTable
      label="What this run will do, one row per account"
      rows={rows}
      columns={columns}
      rowKey={(row) => row.account.id}
      density="compact"
      showTotals
    />
  )
}

/* --------------------------------------------------------------- running -- */

function RunningView({
  state,
  busy,
  stopping,
  onStop,
  onClose,
}: {
  state: BulkRunState | null
  busy: boolean
  stopping: boolean
  onStop: () => void
  onClose: () => void
}) {
  const rows = state?.rows ?? []
  const done = rows.filter(
    (row) => row.outcome.state !== 'pending' && row.outcome.state !== 'moving',
  ).length

  return (
    <Modal
      open
      size="lg"
      onClose={onClose}
      closeDisabled
      title={`Moving ${count(Math.min(done + 1, rows.length))} of ${count(rows.length)}`}
      subtitle="Each row is checked against its own cap as it is reached. Nothing here is optimistic — every figure is quoted from the ledger's reply."
      footer={
        <>
          <span className="w7-spacer" />
          <Button variant="secondary" busy={stopping} disabled={!busy} onClick={onStop}>
            {stopping ? 'Stopping…' : 'Stop after this row'}
          </Button>
        </>
      }
    >
      <div className="w7-bulk-progress">
        <div className="w7-bulk-progress-head">
          <Meter
            value={rows.length ? done / rows.length : 0}
            label={`${done} of ${rows.length} rows done`}
          />
          <span className="w7-mono">{money(state?.movedTotal ?? 0)} moved</span>
        </div>

        {/* ONE short live region. Announcing every row is unusable noise; the
            per-row list is in the DOM and readable on demand. */}
        <span className="w7-sr" aria-live="polite">
          {done} of {rows.length} done, {money(state?.movedTotal ?? 0)} moved
        </span>

        <ul className="w7-bulk-list">
          {rows.map((row) => (
            <RunRow key={row.account.id} row={row} />
          ))}
        </ul>
      </div>
    </Modal>
  )
}

function RunRow({ row }: { row: BulkRunRow }) {
  const outcome = row.outcome
  const verb = row.direction === 'take' ? 'took' : 'gave'
  return (
    <li className="w7-bulk-row" data-state={outcome.state}>
      <span className="w7-bulk-row-icon" data-tone={iconTone(outcome.state)}>
        {outcome.state === 'moving' ? (
          <Spinner size={13} />
        ) : outcome.state === 'settled' ? (
          <Icon name="check" size={13} />
        ) : outcome.state === 'partial' ? (
          <Icon name="warning" size={13} />
        ) : outcome.state === 'failed' ? (
          <Icon name="error" size={13} />
        ) : outcome.state === 'skipped' ? (
          <Icon name="minus" size={13} />
        ) : (
          <Icon name="clock" size={13} />
        )}
      </span>
      <span className="w7-bulk-row-name">{row.account.username}</span>
      <span className="w7-bulk-row-detail">
        {outcome.state === 'moving'
          ? `${row.direction === 'take' ? 'taking' : 'giving'} ${money(row.planned)}…`
          : outcome.state === 'settled'
            ? `${verb} ${money(outcome.moved)} · settled`
            : outcome.state === 'partial'
              ? `${verb} ${money(outcome.moved)} · ${money(outcome.remaining)} left`
              : outcome.state === 'skipped'
                ? outcome.reason
                : outcome.state === 'failed'
                  ? outcome.reason
                  : outcome.state === 'not-attempted'
                    ? 'not attempted'
                    : 'waiting'}
      </span>
      {outcome.state === 'settled' || outcome.state === 'partial' ? (
        <span className="w7-bulk-row-ref">ref {outcome.ref}</span>
      ) : null}
    </li>
  )
}

/* ---------------------------------------------------------------- report -- */

interface ReportSummary {
  total: number
  settled: number
  moved: number
  movedRows: number
  outstanding: number
  openRows: number
  retryable: number
}

function summarise(state: BulkRunState): ReportSummary {
  let settled = 0
  let moved = 0
  let movedRows = 0
  let outstanding = 0
  let openRows = 0
  let retryable = 0

  for (const row of state.rows) {
    const outcome = row.outcome
    if (outcome.state === 'settled') {
      settled += 1
      moved += outcome.moved
      movedRows += 1
    } else if (outcome.state === 'partial') {
      moved += outcome.moved
      movedRows += 1
      outstanding += outcome.remaining
      openRows += 1
    } else {
      outstanding += row.intended
      openRows += 1
    }
    if (RETRYABLE.has(outcome.state)) retryable += 1
  }

  return {
    total: state.rows.length,
    settled,
    moved: round2(moved),
    movedRows,
    outstanding: round2(outstanding),
    openRows,
    retryable,
  }
}

function ReportView({
  state,
  onClose,
  onRetry,
  onDone,
}: {
  state: BulkRunState
  onClose: () => void
  onRetry: () => void
  onDone: () => void
}) {
  const report = summarise(state)
  const group = (...states: BulkRunRow['outcome']['state'][]) =>
    state.rows.filter((row) => states.includes(row.outcome.state))

  return (
    <Modal
      open
      size="lg"
      onClose={onClose}
      title={`Settled ${count(report.settled)} of ${count(report.total)}`}
      subtitle={`${money(report.moved)} moved across ${count(report.movedRows)} ${report.movedRows === 1 ? 'account' : 'accounts'}${report.outstanding > 0 ? ` · ${money(report.outstanding)} is still outstanding` : ''}`}
      footer={
        <>
          <Button
            variant="ghost"
            icon="download"
            onClick={() => exportCsv(`w7-bulk-settle-${isoDay(Date.now())}.csv`, toCsv(state))}
          >
            Export CSV
          </Button>
          <span className="w7-spacer" />
          {report.retryable > 0 ? (
            <Button variant="secondary" icon="refresh" onClick={onRetry}>
              Retry the {count(report.retryable)} that did not complete
            </Button>
          ) : null}
          <Button variant="primary" onClick={onDone}>
            Done
          </Button>
        </>
      }
    >
      <div className="w7-col" style={{ gap: 'var(--w7-s4)' }}>
        <span role="status" className="w7-sr">
          Settled {report.settled} of {report.total}. {money(report.moved)} moved.
          {report.outstanding > 0 ? ` ${money(report.outstanding)} still outstanding.` : ''}
        </span>

        <ReportGroup title="Settled in full" rows={group('settled')} />
        <ReportGroup title="Partly settled" rows={group('partial')} />
        <ReportGroup title="Not moved" rows={group('skipped', 'not-attempted')} />
        <ReportGroup title="Failed" rows={group('failed')} />
      </div>
    </Modal>
  )
}

/** A zero-count group is noise, so an empty group renders nothing at all. */
function ReportGroup({ title, rows }: { title: string; rows: BulkRunRow[] }) {
  if (rows.length === 0) return null
  return (
    <div className="w7-bulk-group">
      <p className="w7-label">
        {title} ({count(rows.length)})
      </p>
      <ul className="w7-bulk-list">
        {rows.map((row) => (
          <RunRow key={row.account.id} row={row} />
        ))}
      </ul>
    </div>
  )
}

function toCsv(state: BulkRunState): Record<string, unknown>[] {
  return state.rows.map((row) => {
    const outcome = row.outcome
    const moved =
      outcome.state === 'settled' || outcome.state === 'partial' ? outcome.moved : 0
    const remaining =
      outcome.state === 'settled'
        ? 0
        : outcome.state === 'partial'
          ? outcome.remaining
          : row.intended
    const reason =
      outcome.state === 'partial' || outcome.state === 'skipped' || outcome.state === 'failed'
        ? outcome.reason
        : outcome.state === 'not-attempted'
          ? 'the run stopped before this row'
          : ''
    return {
      Username: row.account.username,
      Direction: row.direction === 'take' ? 'Take' : 'Give',
      Outstanding: row.intended,
      Moved: moved,
      Remaining: remaining,
      Outcome: outcome.state,
      Reason: reason,
      Ref: outcome.state === 'settled' || outcome.state === 'partial' ? outcome.ref : '',
    }
  })
}

/* ----------------------------------------------------------------- utils -- */

/**
 * Money palette by direction: a take is credit you collect (green), a give is
 * credit you pay (red). `<Money signed />` derives the tone from the sign, so
 * the sign has to carry the direction rather than the magnitude.
 */
const signed = (direction: 'give' | 'take', amount: number): number =>
  direction === 'take' ? amount : -amount

const capitalise = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1)

const iconTone = (state: BulkRunRow['outcome']['state']): string | undefined => {
  if (state === 'settled') return 'profit'
  if (state === 'failed') return 'loss'
  if (state === 'partial' || state === 'skipped') return 'warn'
  return undefined
}
